import { useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  ApiCallError,
  type ApiKey,
  type ApiKeyCreated,
  type ApiKeyScope,
  createApiKey,
  listApiKeys,
  revokeApiKey,
} from "./api";

// Block 7 — API key management panel. Mounted inside Dashboard.tsx.
//
// Surface:
//   - List of keys (active first, revoked grouped at the bottom).
//   - "Create new key" form with name + scope toggles.
//   - Modal-style "one-time secret" panel after create — the only place
//     the plaintext is ever shown. User must click "I copied it" to
//     clear it from React state.
//   - Per-row "Revoke" with inline confirm.
//
// Design notes:
//   - We never persist the plaintext outside component state. Closing
//     the panel or losing focus drops it.
//   - revokeApiKey() returning 204 covers both "owned + revoked" and
//     "not owned / not found" — the dashboard treats success
//     uniformly as "row is now revoked or never existed."
//   - listApiKeys() is re-fetched after every create / revoke; the API
//     is the source of truth for the rendered list.

const SCOPE_LABELS: Record<ApiKeyScope, string> = {
  "stamp:write": "Submit stamps + read own stamps",
  "verify:read": "Read receipts + verify-side",
};

const ALL_SCOPES: ApiKeyScope[] = ["stamp:write", "verify:read"];

function formatTs(ms: number | null): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString();
}

export function ApiKeysPanel() {
  const { getToken } = useAuth();
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newScopes, setNewScopes] = useState<ApiKeyScope[]>(["stamp:write"]);
  const [revealed, setRevealed] = useState<ApiKeyCreated | null>(null);
  const [busyRevoke, setBusyRevoke] = useState<string | null>(null);

  async function reload() {
    setListError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const result = await listApiKeys(token);
      setKeys(result);
    } catch (err) {
      setListError(
        err instanceof Error ? err.message : "failed to load API keys",
      );
    }
  }

  useEffect(() => {
    void reload();
    // Intentionally empty deps — getToken is a stable Clerk ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    if (newName.trim().length === 0) {
      setCreateError("Name is required");
      return;
    }
    if (newScopes.length === 0) {
      setCreateError("Select at least one scope");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("no session token");
      const created = await createApiKey(
        { name: newName.trim(), scopes: newScopes },
        token,
      );
      setRevealed(created);
      setNewName("");
      setNewScopes(["stamp:write"]);
      await reload();
    } catch (err) {
      const msg =
        err instanceof ApiCallError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  }

  async function doRevoke(id: string) {
    if (!window.confirm(`Revoke key ${id}? This cannot be undone.`)) return;
    setBusyRevoke(id);
    try {
      const token = await getToken();
      if (!token) throw new Error("no session token");
      await revokeApiKey(id, token);
      await reload();
    } catch (err) {
      const msg =
        err instanceof ApiCallError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      setListError(msg);
    } finally {
      setBusyRevoke(null);
    }
  }

  function toggleScope(s: ApiKeyScope) {
    setNewScopes((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  return (
    <section className="card" style={{ marginTop: 24 }}>
      <h2 style={{ marginTop: 0 }}>API keys</h2>
      <p className="muted">
        Long-lived tokens for SDK / CI callers. Use{" "}
        <code>Authorization: Bearer fv_live_…</code>. The secret is shown ONCE
        on create — store it immediately or revoke + re-mint.
      </p>

      {revealed && (
        <div
          className="card"
          style={{
            background: "var(--ok-bg, #f5fff5)",
            border: "1px solid var(--ok, #2a8)",
            marginBottom: 16,
          }}
        >
          <div style={{ fontWeight: 600 }}>
            New key created — copy now (shown once)
          </div>
          <pre
            style={{
              padding: 12,
              background: "#fff",
              border: "1px solid #ccc",
              overflowX: "auto",
              userSelect: "all",
            }}
          >
            {revealed.plaintext}
          </pre>
          <div className="muted" style={{ marginBottom: 8 }}>
            Name: <strong>{revealed.name}</strong> · Scopes:{" "}
            {revealed.scopes.join(", ")} · Id: <code>{revealed.id}</code>
          </div>
          <button type="button" onClick={() => setRevealed(null)}>
            I copied it — clear from screen
          </button>
        </div>
      )}

      <form onSubmit={submitCreate} style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 8, alignItems: "flex-end" }}>
          <label style={{ flex: 1 }}>
            <div className="muted" style={{ marginBottom: 4 }}>
              Name
            </div>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Production CI"
              maxLength={120}
              style={{ width: "100%" }}
              disabled={creating}
            />
          </label>
          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <div className="muted" style={{ marginBottom: 4 }}>
              Scopes
            </div>
            {ALL_SCOPES.map((s) => (
              <label key={s} style={{ marginRight: 12 }}>
                <input
                  type="checkbox"
                  checked={newScopes.includes(s)}
                  onChange={() => toggleScope(s)}
                  disabled={creating}
                />{" "}
                <code>{s}</code>
              </label>
            ))}
          </fieldset>
          <button type="submit" disabled={creating}>
            {creating ? "Minting…" : "Mint key"}
          </button>
        </div>
        <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
          {newScopes.length > 0
            ? newScopes
                .map((s) => `${s}: ${SCOPE_LABELS[s]}`)
                .join(" · ")
            : "Pick at least one scope."}
        </div>
        {createError && (
          <p className="err" style={{ marginTop: 8 }}>
            {createError}
          </p>
        )}
      </form>

      {listError && <p className="err">{listError}</p>}
      {keys === null && !listError && <p className="muted">Loading…</p>}
      {keys !== null && keys.length === 0 && (
        <p className="muted">
          No API keys yet. Mint one above for SDK / CI callers.
        </p>
      )}
      {keys !== null && keys.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: 4 }}>Name</th>
              <th style={{ textAlign: "left", padding: 4 }}>Prefix</th>
              <th style={{ textAlign: "left", padding: 4 }}>Scopes</th>
              <th style={{ textAlign: "left", padding: 4 }}>Status</th>
              <th style={{ textAlign: "left", padding: 4 }}>Created</th>
              <th style={{ textAlign: "left", padding: 4 }}>Last used</th>
              <th style={{ padding: 4 }} />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr
                key={k.id}
                style={{
                  borderTop: "1px solid #eee",
                  opacity: k.status === "revoked" ? 0.5 : 1,
                }}
              >
                <td style={{ padding: 4 }}>{k.name}</td>
                <td style={{ padding: 4 }}>
                  <code>fv_live_{k.prefix}…</code>
                </td>
                <td style={{ padding: 4, fontSize: 12 }}>
                  {k.scopes.join(", ")}
                </td>
                <td style={{ padding: 4 }}>{k.status}</td>
                <td style={{ padding: 4 }}>{formatTs(k.created_at)}</td>
                <td style={{ padding: 4 }}>{formatTs(k.last_used_at)}</td>
                <td style={{ padding: 4 }}>
                  {k.status === "active" && (
                    <button
                      type="button"
                      className="secondary"
                      disabled={busyRevoke === k.id}
                      onClick={() => doRevoke(k.id)}
                    >
                      {busyRevoke === k.id ? "…" : "Revoke"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
